import React, { useEffect, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { auth, db } from '../firebase';
import { doc, getDoc, updateDoc, arrayUnion } from 'firebase/firestore';
import { User, Building2, Briefcase, Phone, ShieldCheck } from 'lucide-react';
import { EcointelLottieMark } from '../components/EcoIntelLoader';
import AddressFieldsSection from '../components/AddressFieldsSection';
import {
  ADDRESS_FIELD_DEFAULTS,
  composeFullAddress,
  profileDataToAddressState,
  isAddressComplete,
} from '../utils/profileAddress';
import { useUserFacingError } from '../hooks/useUserFacingError';
import { LEGAL_LINKS } from '../constants/legalLinks';
import {
  BUSINESS_TYPES,
  BUSINESS_TYPE_OTHER,
  ONBOARDING_CONSENTS,
  TERMS_PRIVACY_CONSENTS,
  TERMS_PRIVACY_LABEL,
  isValidBusinessType,
  buildConsentRecord,
} from '../constants/onboardingConsents';
import {
  INDUSTRIES,
  INDUSTRY_OTHER,
  BUSINESS_SIZES,
  REFERRAL_SOURCES,
  REFERRAL_SOURCE_OTHER,
  isValidIndustry,
  isValidBusinessSize,
  isValidReferralSource,
} from '../constants/onboardingProfileFields';

export default function CompleteProfile() {
  const navigate = useNavigate();
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [displayName, setDisplayName] = useState('');
  const [email, setEmail] = useState('');
  const [preferredUsername, setPreferredUsername] = useState('');
  const [phoneNumber, setPhoneNumber] = useState('');
  const [addressFields, setAddressFields] = useState(() => ({ ...ADDRESS_FIELD_DEFAULTS }));
  const [businessName, setBusinessName] = useState('');
  const [businessType, setBusinessType] = useState('');
  const [businessTypeOther, setBusinessTypeOther] = useState('');
  const [industry, setIndustry] = useState('');
  const [industryOther, setIndustryOther] = useState('');
  const [businessSize, setBusinessSize] = useState('');
  const [referralSource, setReferralSource] = useState('');
  const [referralSourceOther, setReferralSourceOther] = useState('');
  const [jobTitle, setJobTitle] = useState('');
  // B2B legal verification (brief 2026-06-11). Both required ticks default
  // UNTICKED — they are the s2(4) evidence and the click-wrap, so the user
  // must actively grant them. Marketing is optional and pre-unticked.
  const [businessPurposeAccepted, setBusinessPurposeAccepted] = useState(false);
  const [termsAccepted, setTermsAccepted] = useState(false);
  const [marketingOptIn, setMarketingOptIn] = useState(false);
  const [error, setError] = useState('');
  const { formatError, logError } = useUserFacingError();

  useEffect(() => {
    const u = auth.currentUser;
    if (!u) {
      navigate('/login', { replace: true });
      return;
    }
    (async () => {
      try {
        const snap = await getDoc(doc(db, 'users', u.uid));
        const d = snap.data() || {};
        const handle = d.preferredUsername || '';
        setPreferredUsername(handle);
        setDisplayName(d.displayName || u.displayName || handle || '');
        setEmail(u.email || d.email || '');
        setPhoneNumber(d.phoneNumber || '');
        setAddressFields(profileDataToAddressState(d));
        setBusinessName(d.businessName || '');
        setBusinessType(isValidBusinessType(d.businessType) ? d.businessType : '');
        setBusinessTypeOther(d.businessTypeOther || '');
        setIndustry(isValidIndustry(d.industry) ? d.industry : '');
        setIndustryOther(d.industryOther || '');
        setBusinessSize(isValidBusinessSize(d.businessSize) ? d.businessSize : '');
        setReferralSource(isValidReferralSource(d.referralSource) ? d.referralSource : '');
        setReferralSourceOther(d.referralSourceOther || '');
        if (d.profileComplete === true) {
          navigate('/dashboard', { replace: true });
          return;
        }
      } catch (e) {
        console.error(e);
        setError('Could not load profile.');
      } finally {
        setLoading(false);
      }
    })();
  }, [navigate]);

  const handleSubmit = async (e) => {
    e.preventDefault();
    setError('');
    const u = auth.currentUser;
    if (!u) return;

    const name = displayName.trim();
    const phone = phoneNumber.trim();
    const company = businessName.trim();
    const title = jobTitle.trim();
    const otherDetail = businessTypeOther.trim();

    if (!name || !phone || !company || !title) {
      setError('Please fill in all fields.');
      return;
    }
    if (!businessType) {
      setError('Please select your business type.');
      return;
    }
    if (businessType === BUSINESS_TYPE_OTHER && !otherDetail) {
      setError('Please specify your business type.');
      return;
    }
    if (!industry) {
      setError('Please select your industry.');
      return;
    }
    if (industry === INDUSTRY_OTHER && !industryOther.trim()) {
      setError('Please specify your industry.');
      return;
    }
    if (!businessSize) {
      setError('Please select your business size.');
      return;
    }
    if (!referralSource) {
      setError('Please tell us where you heard about us.');
      return;
    }
    if (referralSource === REFERRAL_SOURCE_OTHER && !referralSourceOther.trim()) {
      setError('Please specify where you heard about us.');
      return;
    }
    if (!isAddressComplete(addressFields)) {
      setError('Please complete street address, city, postcode, and country.');
      return;
    }
    if (!businessPurposeAccepted) {
      setError('Please confirm you are creating this account for business and/or professional purposes.');
      return;
    }
    if (!termsAccepted) {
      setError('Please agree to the Terms of Service and Privacy Policy to continue.');
      return;
    }

    const fullAddress = composeFullAddress(addressFields);

    // Append-only legal-evidence consent records. Each carries wording
    // version + timestamp + user (brief 2026-06-11). The single ToS/Privacy
    // checkbox logs two records (one per document) so each is independently
    // auditable to its version. A marketing record is always written so the
    // user's explicit choice (true or false) is on record from onboarding.
    const nowIso = new Date().toISOString();
    const consentRecords = [
      buildConsentRecord(ONBOARDING_CONSENTS.business_purpose, { uid: u.uid, granted: true, nowIso }),
      ...TERMS_PRIVACY_CONSENTS.map((def) =>
        buildConsentRecord(def, { uid: u.uid, granted: true, nowIso, wording: TERMS_PRIVACY_LABEL })
      ),
      buildConsentRecord(ONBOARDING_CONSENTS.marketing_emails, {
        uid: u.uid,
        granted: marketingOptIn,
        nowIso,
        // v1.1 splits label (short) + description (full); snapshot the full text.
        wording: ONBOARDING_CONSENTS.marketing_emails.description,
      }),
    ];

    setSaving(true);
    try {
      const profilePatch = {
        displayName: name,
        phoneNumber: phone,
        address: fullAddress,
        addressLine1: addressFields.addressLine1.trim(),
        addressLine2: addressFields.addressLine2.trim(),
        addressCity: addressFields.addressCity.trim(),
        addressRegion: addressFields.addressRegion.trim(),
        addressPostcode: addressFields.addressPostcode.trim(),
        addressCountry: addressFields.addressCountry.trim(),
        businessName: company,
        businessType,
        businessTypeOther: businessType === BUSINESS_TYPE_OTHER ? otherDetail : '',
        industry,
        industryOther: industry === INDUSTRY_OTHER ? industryOther.trim() : '',
        businessSize,
        referralSource,
        referralSourceOther: referralSource === REFERRAL_SOURCE_OTHER ? referralSourceOther.trim() : '',
        jobTitle: title,
        marketingOptIn,
        profileComplete: true,
        productTourStatus: 'pending',
        consents: arrayUnion(...consentRecords),
      };
      if (u.email) profilePatch.email = u.email;
      await updateDoc(doc(db, 'users', u.uid), profilePatch);
      navigate('/dashboard', { replace: true });
    } catch (err) {
      logError('CompleteProfile save failed', err);
      setError(formatError(err) || 'Could not save profile.');
    } finally {
      setSaving(false);
    }
  };

  if (loading) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-slate-50">
        <p className="text-slate-600 text-sm">Loading…</p>
      </div>
    );
  }

  return (
    <div className="min-h-screen flex flex-col items-center justify-center bg-slate-50 p-4 py-10">
      <div className="max-w-lg w-full bg-white rounded-2xl shadow-sm border border-slate-200 p-8">
        <div className="flex items-center gap-3 mb-2">
          <div className="w-10 h-10 bg-[#043132]/10 rounded-full flex items-center justify-center text-[#043132]">
            <User size={22} />
          </div>
          <h1 className="text-xl font-bold text-slate-800">Complete your profile</h1>
        </div>
        <p className="text-sm text-slate-600 mb-4">
          We need a few details before you subscribe or use the platform. This information is stored with your account.
        </p>

        <div className="mb-6 rounded-lg border border-[#043132]/15 bg-[#043132]/5 p-3 text-sm text-slate-700">
          EcoIntel is a business-to-business service. We&apos;re required by law to make sure accounts
          are created for business or professional use, not consumer use, so please confirm a few
          details about your organisation below.
        </div>

        <form onSubmit={handleSubmit} className="space-y-4">
          <div>
            <label className="block text-xs font-semibold text-slate-500 uppercase tracking-wide mb-1">Full name *</label>
            <input
              type="text"
              value={displayName}
              onChange={(e) => setDisplayName(e.target.value)}
              className="w-full border border-slate-300 rounded-lg px-3 py-2 text-sm focus:border-[#043132] focus:ring-1 focus:ring-[#043132] outline-none"
              required
              autoComplete="name"
            />
          </div>
          <div>
            <label className="block text-xs font-semibold text-slate-500 uppercase tracking-wide mb-1">
              {email ? 'Email' : 'Sign-in handle'}
            </label>
            <input
              type={email ? 'email' : 'text'}
              value={email || preferredUsername || ''}
              readOnly
              className="w-full border border-slate-200 rounded-lg px-3 py-2 text-sm bg-slate-50 text-slate-600"
            />
            <p className="text-[11px] text-slate-400 mt-1">
              {email
                ? 'From your sign-in provider. Must be verified.'
                : preferredUsername
                  ? 'Your work account did not provide an email. This handle is from your sign-in provider.'
                  : 'Your sign-in provider did not provide an email.'}
            </p>
          </div>
          <div>
            <label className="block text-xs font-semibold text-slate-500 uppercase tracking-wide mb-1 flex items-center gap-1">
              <Phone size={12} /> Phone number *
            </label>
            <input
              type="tel"
              value={phoneNumber}
              onChange={(e) => setPhoneNumber(e.target.value)}
              className="w-full border border-slate-300 rounded-lg px-3 py-2 text-sm focus:border-[#043132] focus:ring-1 focus:ring-[#043132] outline-none"
              required
              autoComplete="tel"
            />
          </div>
          <AddressFieldsSection
            value={addressFields}
            onChange={(patch) => setAddressFields((prev) => ({ ...prev, ...patch }))}
            required
          />
          <div>
            <label className="block text-xs font-semibold text-slate-500 uppercase tracking-wide mb-1 flex items-center gap-1">
              <Building2 size={12} /> Company / Business name *
            </label>
            <input
              type="text"
              value={businessName}
              onChange={(e) => setBusinessName(e.target.value)}
              className="w-full border border-slate-300 rounded-lg px-3 py-2 text-sm focus:border-[#043132] focus:ring-1 focus:ring-[#043132] outline-none"
              required
              autoComplete="organization"
            />
            <p className="text-[11px] text-slate-400 mt-1">
              Sole traders and partnerships can use their own name or trading name.
            </p>
          </div>
          <div>
            <label className="block text-xs font-semibold text-slate-500 uppercase tracking-wide mb-1 flex items-center gap-1">
              <Building2 size={12} /> Business type *
            </label>
            <select
              value={businessType}
              onChange={(e) => setBusinessType(e.target.value)}
              className="w-full border border-slate-300 rounded-lg px-3 py-2 text-sm bg-white focus:border-[#043132] focus:ring-1 focus:ring-[#043132] outline-none"
              required
            >
              <option value="" disabled>Select your business type…</option>
              {BUSINESS_TYPES.map((t) => (
                <option key={t.value} value={t.value}>{t.label}</option>
              ))}
            </select>
            {businessType === BUSINESS_TYPE_OTHER && (
              <input
                type="text"
                value={businessTypeOther}
                onChange={(e) => setBusinessTypeOther(e.target.value)}
                placeholder="Please specify your business type"
                className="w-full mt-2 border border-slate-300 rounded-lg px-3 py-2 text-sm focus:border-[#043132] focus:ring-1 focus:ring-[#043132] outline-none"
                required
              />
            )}
          </div>
          <div>
            <label className="block text-xs font-semibold text-slate-500 uppercase tracking-wide mb-1">
              What industry are you in? *
            </label>
            <select
              value={industry}
              onChange={(e) => setIndustry(e.target.value)}
              className="w-full border border-slate-300 rounded-lg px-3 py-2 text-sm bg-white focus:border-[#043132] focus:ring-1 focus:ring-[#043132] outline-none"
              required
            >
              <option value="" disabled>Select your industry…</option>
              {INDUSTRIES.map((t) => (
                <option key={t.value} value={t.value}>{t.label}</option>
              ))}
            </select>
            {industry === INDUSTRY_OTHER && (
              <input
                type="text"
                value={industryOther}
                onChange={(e) => setIndustryOther(e.target.value)}
                placeholder="Please specify your industry"
                className="w-full mt-2 border border-slate-300 rounded-lg px-3 py-2 text-sm focus:border-[#043132] focus:ring-1 focus:ring-[#043132] outline-none"
                required
              />
            )}
          </div>
          <div>
            <label className="block text-xs font-semibold text-slate-500 uppercase tracking-wide mb-1">
              What size business do you work for? *
            </label>
            <select
              value={businessSize}
              onChange={(e) => setBusinessSize(e.target.value)}
              className="w-full border border-slate-300 rounded-lg px-3 py-2 text-sm bg-white focus:border-[#043132] focus:ring-1 focus:ring-[#043132] outline-none"
              required
            >
              <option value="" disabled>Select business size…</option>
              {BUSINESS_SIZES.map((t) => (
                <option key={t.value} value={t.value}>{t.label}</option>
              ))}
            </select>
          </div>
          <div>
            <label className="block text-xs font-semibold text-slate-500 uppercase tracking-wide mb-1">
              Where did you hear from us? *
            </label>
            <select
              value={referralSource}
              onChange={(e) => setReferralSource(e.target.value)}
              className="w-full border border-slate-300 rounded-lg px-3 py-2 text-sm bg-white focus:border-[#043132] focus:ring-1 focus:ring-[#043132] outline-none"
              required
            >
              <option value="" disabled>Select an option…</option>
              {REFERRAL_SOURCES.map((t) => (
                <option key={t.value} value={t.value}>{t.label}</option>
              ))}
            </select>
            {referralSource === REFERRAL_SOURCE_OTHER && (
              <input
                type="text"
                value={referralSourceOther}
                onChange={(e) => setReferralSourceOther(e.target.value)}
                placeholder="Please specify"
                className="w-full mt-2 border border-slate-300 rounded-lg px-3 py-2 text-sm focus:border-[#043132] focus:ring-1 focus:ring-[#043132] outline-none"
                required
              />
            )}
          </div>
          <div>
            <label className="block text-xs font-semibold text-slate-500 uppercase tracking-wide mb-1 flex items-center gap-1">
              <Briefcase size={12} /> Job title *
            </label>
            <input
              type="text"
              value={jobTitle}
              onChange={(e) => setJobTitle(e.target.value)}
              className="w-full border border-slate-300 rounded-lg px-3 py-2 text-sm focus:border-[#043132] focus:ring-1 focus:ring-[#043132] outline-none"
              required
              autoComplete="organization-title"
            />
          </div>

          <div className="space-y-3 rounded-lg border border-slate-200 bg-slate-50 p-4">
            <div className="flex items-center gap-1.5 text-xs font-semibold text-slate-500 uppercase tracking-wide">
              <ShieldCheck size={13} className="text-[#043132]" /> Confirmations
            </div>
            <label className="flex items-start gap-2.5 text-sm text-slate-700 cursor-pointer">
              <input
                type="checkbox"
                checked={businessPurposeAccepted}
                onChange={(e) => setBusinessPurposeAccepted(e.target.checked)}
                className="mt-0.5 h-4 w-4 shrink-0 rounded border-slate-300 text-[#043132] focus:ring-[#043132]"
              />
              <span>{ONBOARDING_CONSENTS.business_purpose.label} <span className="text-red-500">*</span></span>
            </label>
            <label className="flex items-start gap-2.5 text-sm text-slate-700 cursor-pointer">
              <input
                type="checkbox"
                checked={termsAccepted}
                onChange={(e) => setTermsAccepted(e.target.checked)}
                className="mt-0.5 h-4 w-4 shrink-0 rounded border-slate-300 text-[#043132] focus:ring-[#043132]"
              />
              <span>
                I agree to the{' '}
                <a href={LEGAL_LINKS.terms} target="_blank" rel="noopener noreferrer" className="text-[#3d9dad] underline underline-offset-2 hover:text-[#065354]">Terms of Service</a>{' '}
                and have read the{' '}
                <a href={LEGAL_LINKS.privacy} target="_blank" rel="noopener noreferrer" className="text-[#3d9dad] underline underline-offset-2 hover:text-[#065354]">Privacy Policy</a>.
                <span className="text-red-500"> *</span>
              </span>
            </label>
            <label className="flex items-start gap-2.5 text-sm text-slate-700 cursor-pointer">
              <input
                type="checkbox"
                checked={marketingOptIn}
                onChange={(e) => setMarketingOptIn(e.target.checked)}
                className="mt-0.5 h-4 w-4 shrink-0 rounded border-slate-300 text-[#043132] focus:ring-[#043132]"
              />
              <span>
                <span className="font-medium">{ONBOARDING_CONSENTS.marketing_emails.label}</span>
                <span className="block text-[11px] text-slate-400 mt-0.5">{ONBOARDING_CONSENTS.marketing_emails.description}</span>
              </span>
            </label>
          </div>

          {error && <p className="text-sm text-red-600">{error}</p>}

          <button
            type="submit"
            disabled={saving}
            className="w-full py-3 px-4 bg-[#043132] hover:bg-[#065354] text-white font-semibold rounded-xl transition-colors flex items-center justify-center gap-2 disabled:opacity-50"
          >
            {saving ? <EcointelLottieMark size={20} /> : null}
            {saving ? 'Saving…' : 'Save and continue'}
          </button>
        </form>
      </div>
    </div>
  );
}
